/**
 * Thin fetch wrappers — same endpoints/methods as raw fetch today.
 * No caching, no retry. Throws on non-OK for Toast-friendly errors.
 */

export class ApiError extends Error {
  status: number
  constructor(status: number, message: string) {
    super(message)
    this.name = 'ApiError'
    this.status = status
  }
}

async function parseJson<T>(res: Response): Promise<T> {
  const text = await res.text()
  if (!text) return undefined as T
  try {
    return JSON.parse(text) as T
  } catch {
    return undefined as T
  }
}

export async function parseErrorResponse(res: Response): Promise<ApiError> {
  const text = await res.text()
  const contentType = res.headers.get('content-type') ?? ''
  if (contentType.includes('application/problem+json')) {
    try {
      const problem = JSON.parse(text) as { detail?: string; title?: string }
      return new ApiError(
        res.status,
        problem.detail || problem.title || `HTTP ${res.status}`,
      )
    } catch {
      // claimed problem+json but body did not parse — fall through
    }
  }
  return new ApiError(res.status, text || `HTTP ${res.status}`)
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const res = await fetch(path, {
    method,
    credentials: 'same-origin',
    headers: body !== undefined ? { 'Content-Type': 'application/json' } : undefined,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  })
  if (!res.ok) {
    throw await parseErrorResponse(res)
  }
  if (res.status === 204) return undefined as T
  return parseJson<T>(res)
}

export function apiGet<T>(path: string): Promise<T> {
  return request<T>('GET', path)
}

export function apiPost<T>(path: string, body?: unknown): Promise<T> {
  return request<T>('POST', path, body)
}

export function apiPatch<T>(path: string, body?: unknown): Promise<T> {
  return request<T>('PATCH', path, body)
}

export function apiPut<T>(path: string, body?: unknown): Promise<T> {
  return request<T>('PUT', path, body)
}

export function apiDelete(path: string, body?: unknown): Promise<void> {
  return request<void>('DELETE', path, body)
}

/** OpenAPI-derived types for endpoints converted in G33 (regenerate via npm run generate-api). */
export type { components } from './control-plane-api'
export type Tenant = import('./control-plane-api').components['schemas']['Tenant']
export type Problem = import('./control-plane-api').components['schemas']['Problem']
export type PagedResources = import('./control-plane-api').components['schemas']['PagedResources']
